import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass, unref as _unref } from "vue"

const _hoisted_1 = { class: "page-shell" }
const _hoisted_2 = { class: "result-info" }
const _hoisted_3 = {
  key: 0,
  class: "result-error"
}
const _hoisted_4 = {
  key: 1,
  class: "result-info"
}
const _hoisted_5 = { class: "quote-list" }
const _hoisted_6 = { class: "quote-left" }
const _hoisted_7 = ["src", "alt"]
const _hoisted_8 = { class: "quote-copy" }
const _hoisted_9 = { class: "quote-top" }
const _hoisted_10 = { class: "quote-title" }
const _hoisted_11 = { class: "quote-subtitle" }
const _hoisted_12 = { class: "quote-meta" }
const _hoisted_13 = { class: "quote-price" }

import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import AppTab from "@/shared/components/ui/AppTab.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import { fetchVendorQuotations } from "@/shared/services/quotations.api";
import AppCard from "@/shared/components/ui/AppCard.vue";
import { getStatusColor } from "@/shared/utils/utils";

type QuoteItem = {
  id: number;
  vendor: string;
  vendorLogo: string;
  service: string;
  amount: string;
  date: string;
  status: string;
  iconTone: string;
  quotes_count: number;
};

type QuoteApiRecord = Record<string, unknown>;
type QuoteQueryParams = Record<string, string>;
type QuoteListMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'QuotationPage',
  setup(__props) {

const tabs = [
  { label: "Received", value: "new" },
  { label: "Accepted", value: "accepted" },
  { label: "Declined", value: "rejected" },
  { label: "All", value: "all" },
];
const activeTab = ref("new");
const searchQuery = ref("");
const isLoading = ref(false);
const errorMessage = ref("");
const router = useRouter();

const quotes = ref<QuoteItem[]>([]);
const pagination = ref<QuoteListMeta>({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
});
const hasMore = ref(false);
const isLoadingMore = ref(false);
let loadRequestId = 0;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const statusStyleMap: Record<string, { iconTone: string }> = {
  received: { iconTone: "quote-icon--peach" },
  accepted: { iconTone: "quote-icon--mint" },
  declined: { iconTone: "quote-icon--rose" },
};

function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function normalizeQuote(item: QuoteApiRecord): QuoteItem {
  const rawStatus = asString(item.status, "Received");
  const status = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
  const style = statusStyleMap[rawStatus.toLowerCase()] || statusStyleMap.received;

  return {
    id: Number(item.id) || 0,
    vendor: asString(item.vendor_name || item.vendor || item.provider_name, "Vendor"),
    vendorLogo: asString(item.vendor_logo || item.logo || item.vendor_image, ""),
    service: asString(item.service_name || item.service || item.title, "Service"),
    amount: asString(item.amount || item.total_amount || item.price, "-"),
    date: asString(item.date || item.created_at || item.quotation_date, "-"),
    status,
    iconTone: style.iconTone,
    quotes_count: Number(item.quotes_count) || 0,
  };
}

function extractQuoteList(payload: unknown): QuoteApiRecord[] {
  if (Array.isArray(payload)) {
    return payload as QuoteApiRecord[];
  }

  const wrapped = payload as { data?: unknown; quotations?: unknown; quotes?: unknown };
  if (Array.isArray(wrapped?.data)) return wrapped.data as QuoteApiRecord[];
  if (Array.isArray(wrapped?.quotations)) return wrapped.quotations as QuoteApiRecord[];
  if (Array.isArray(wrapped?.quotes)) return wrapped.quotes as QuoteApiRecord[];
  return [];
}

function extractMeta(payload: unknown): QuoteListMeta | null {
  const wrapped = payload as { meta?: Record<string, unknown> };
  if (!wrapped?.meta) return null;
  const meta = wrapped.meta;
  const currentPage = Number(meta.current_page || 1);
  const lastPage = Number(meta.last_page || 1);
  const perPage = Number(meta.per_page || 10);
  const total = Number(meta.total || 0);

  return {
    current_page: Number.isNaN(currentPage) ? 1 : currentPage,
    last_page: Number.isNaN(lastPage) ? 1 : lastPage,
    per_page: Number.isNaN(perPage) ? 10 : perPage,
    total: Number.isNaN(total) ? 0 : total,
  };
}

async function loadQuotations(append = false) {
  const requestId = ++loadRequestId;
  const nextPage = append ? pagination.value.current_page + 1 : 1;

  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
  }
  errorMessage.value = "";

  try {
    const params: QuoteQueryParams = {
      per_page: "10",
      page: String(nextPage),
    };
    const query = searchQuery.value.trim();
    if (query) params.search = query;
    if (activeTab.value !== "all") params.status = activeTab.value;

    const response = await fetchVendorQuotations(params);
    if (requestId !== loadRequestId) return;

    const normalized = extractQuoteList(response).map(normalizeQuote);
    quotes.value = append ? [...quotes.value, ...normalized] : normalized;

    const meta = extractMeta(response);
    if (meta) {
      pagination.value = meta;
      hasMore.value = meta.current_page < meta.last_page;
    } else {
      pagination.value = {
        current_page: nextPage,
        last_page: nextPage,
        per_page: 10,
        total: quotes.value.length,
      };
      hasMore.value = false;
    }
  } catch (error) {
    if (requestId !== loadRequestId) return;
    console.error("Failed to load quotations", error);
    errorMessage.value = "Unable to load quotations right now.";
    if (!append) {
      quotes.value = [];
      pagination.value = { current_page: 1, last_page: 1, per_page: 10, total: 0 };
      hasMore.value = false;
    }
  } finally {
    if (requestId !== loadRequestId) return;
    if (append) {
      isLoadingMore.value = false;
    } else {
      isLoading.value = false;
    }
  }
}

// function statusClass(status: string) {
//   return `quote-status--${status.toLowerCase()}`;
// }

function hasValidLogo(url: string) {
  if (!url) return false;
  const normalized = url.toLowerCase();
  return !normalized.includes("noimage.web");
}

function handleOpen(id: number) {
  router.push({ name: "vendor.quotations.detail", params: { id } });
}

function loadMore() {
  if (isLoadingMore.value || !hasMore.value) return;
  loadQuotations(true);
}

onMounted(loadQuotations);

watch(activeTab, () => {
  loadQuotations();
});

watch(searchQuery, () => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    loadQuotations();
  }, 350);
});

onBeforeUnmount(() => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(AppTab, {
      modelValue: activeTab.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event)),
      items: tabs,
      columns: 4
    }, null, 8, ["modelValue"]),
    _createElementVNode("div", null, [
      _createVNode(AppTextField, {
        modelValue: searchQuery.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
        "prepend-inner-icon": "mdi-magnify",
        variant: "outlined",
        "hide-details": "",
        placeholder: "Search..."
      }, null, 8, ["modelValue"])
    ]),
    _createElementVNode("p", _hoisted_2, _toDisplayString(pagination.value.total > 0 ? `${quotes.value.length} of ${pagination.value.total} results` : `${quotes.value.length} results`), 1),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_4, "Loading quotations..."))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(quotes.value, (item) => {
        return (_openBlock(), _createBlock(AppCard, {
          key: item.id,
          class: "quote-card",
          onClick: ($event: any) => (handleOpen(item.id))
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", {
                class: _normalizeClass(["quote-icon", [item.iconTone, { 'quote-icon--with-logo': hasValidLogo(item.vendorLogo) }]])
              }, [
                (hasValidLogo(item.vendorLogo))
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: item.vendorLogo,
                      alt: item.vendor,
                      class: "quote-logo"
                    }, null, 8, _hoisted_7))
                  : (_openBlock(), _createBlock(_component_v_icon, {
                      key: 1,
                      size: "18"
                    }, {
                      default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                        _createTextVNode("mdi-store-outline", -1)
                      ]))]),
                      _: 1
                    }))
              ], 2),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("p", _hoisted_10, _toDisplayString(item.vendor), 1),
                  _createVNode(_component_v_chip, {
                    color: _unref(getStatusColor)(item.status),
                    size: "small",
                    label: ""
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(item.status), 1)
                    ]),
                    _: 2
                  }, 1032, ["color"])
                ]),
                _createElementVNode("p", _hoisted_11, _toDisplayString(item.service), 1),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("span", _hoisted_13, _toDisplayString(item.amount), 1),
                  _cache[3] || (_cache[3] = _createElementVNode("span", { class: "dot" }, "•", -1)),
                  _createElementVNode("span", null, _toDisplayString(item.date), 1)
                ])
              ])
            ]),
            _createVNode(_component_v_icon, {
              size: "18",
              class: "quote-chevron"
            }, {
              default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                _createTextVNode("mdi-chevron-right", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 2
        }, 1032, ["onClick"]))
      }), 128))
    ]),
    (hasMore.value)
      ? (_openBlock(), _createBlock(_component_v_btn, {
          key: 2,
          variant: "outlined",
          color: "primary",
          loading: isLoadingMore.value,
          onClick: loadMore
        }, {
          default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
            _createTextVNode(" Load more ", -1)
          ]))]),
          _: 1
        }, 8, ["loading"]))
      : _createCommentVNode("", true)
  ]))
}
}

})